import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ApiError } from '../../../../adapters/api-client/api';
import { formatMoney } from '../../../../commodity/format/money';
import { getCourseReviews } from '../../api/reviews';
import {
  ExploreCourseDetail,
  InterestInput,
  RequestBlock,
  createLearnerRequest,
  getExploreCourse,
  registerInterest,
} from '../../api/learner-explore';
import ReviewsSection from '../../components/ReviewsSection';
import { describeSchedule } from '../../domain/catalog';
import {
  REQUEST_REASON_WORDS,
  REQUEST_STATE_WORDS,
  REQUEST_THIS_CLASS,
  blockExplanation,
  gradeRangeWords,
  isOpenRequest,
} from '../../learner/requestWords';

type Load =
  | { status: 'loading' }
  | { status: 'missing' }
  | { status: 'error' }
  | { status: 'ready'; course: ExploreCourseDetail };

/**
 * UC-S04 — one course as the Student evaluates it (DEC-S2-S04-01..09, S06-01).
 *
 * The same published version the Parent reads. The price is information: nothing
 * here buys, holds a seat or creates an enrollment (DEC-S2-S04-06). Whether the
 * class can be requested is the server's answer, shown with its reason
 * (DEC-S2-S04-08). The request's state is projected, not owned (DEC-S2-S04-09).
 * Course media does not exist yet and nothing stands in for it (DEC-S2-S04-07).
 */
const LearnerExploreCourse: React.FC = () => {
  const { courseId = '' } = useParams<{ courseId: string }>();
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [interest, setInterest] = useState<'idle' | 'sent' | 'failed'>('idle');

  const fetchCourse = useCallback(() => {
    setLoad({ status: 'loading' });
    getExploreCourse(courseId)
      .then((course) => setLoad({ status: 'ready', course }))
      .catch((error: unknown) =>
        setLoad({
          status: error instanceof ApiError && error.status === 404 ? 'missing' : 'error',
        }),
      );
  }, [courseId]);

  useEffect(fetchCourse, [fetchCourse]);

  const request = async (course: ExploreCourseDetail) => {
    if (!course.offer) return;
    setSending(true);
    setRefusal(null);
    try {
      const created = await createLearnerRequest({
        courseId: course.listingId,
        offerId: course.offer.offerId,
      });
      setLoad({ status: 'ready', course: { ...course, request: created } });
    } catch (error) {
      // A stale page is refused, never quietly swapped for another class.
      const code = error instanceof ApiError ? (error.body?.code as string) : undefined;
      setRefusal(
        code
          ? code === 'REQUEST_CHANGED'
            ? 'Esta clase cambió. Mira cómo quedó antes de pedirla.'
            : blockExplanation(code as RequestBlock, course.gradeLevel)
          : 'No pudimos enviar tu solicitud. Prueba otra vez en un momento.',
      );
      if (code) fetchCourse();
    } finally {
      setSending(false);
    }
  };

  const askAnotherTime = (input: InterestInput) => {
    setInterest('idle');
    registerInterest(input)
      .then(() => setInterest('sent'))
      .catch(() => setInterest('failed'));
  };

  return (
    <>
      <p>
        <Link className="lrn-link" to="/student/explore">
          ← Explorar clases
        </Link>
      </p>

      {load.status === 'loading' && (
        <p className="hi-s" role="status">
          Cargando la clase…
        </p>
      )}

      {load.status === 'missing' && (
        <div className="lrn-status" role="status">
          <h2>Esta clase no está disponible</h2>
          <p>Puede que ya no se ofrezca. Hay más para explorar.</p>
        </div>
      )}

      {load.status === 'error' && (
        <div className="lrn-status" role="alert">
          <h2>No pudimos cargar esta clase</h2>
          <p>No es culpa tuya. Prueba otra vez en un momento.</p>
          <button className="ag-b" onClick={fetchCourse}>
            Intentar otra vez
          </button>
        </div>
      )}

      {load.status === 'ready' && (
        <CourseBody
          course={load.course}
          sending={sending}
          refusal={refusal}
          interest={interest}
          onRequest={() => request(load.course)}
          onAskAnotherTime={askAnotherTime}
        />
      )}
    </>
  );
};

const CourseBody: React.FC<{
  course: ExploreCourseDetail;
  sending: boolean;
  refusal: string | null;
  interest: 'idle' | 'sent' | 'failed';
  onRequest: () => void;
  onAskAnotherTime: (input: InterestInput) => void;
}> = ({ course, sending, refusal, interest, onRequest, onAskAnotherTime }) => {
  const open = course.request && isOpenRequest(course.request.state);
  const { requestable, reason } = course.eligibility;
  const offer = course.offer;
  const range = gradeRangeWords(course.gradeLevel);

  return (
    <>
      <div className="lrn-status">
        <h1 className="h2">{course.title}</h1>
        {course.teacher ? (
          <p>
            Con{' '}
            <Link
              className="lrn-link"
              to={`/student/teachers/${encodeURIComponent(course.teacher.teacherId)}`}
            >
              {course.teacher.firstName} {course.teacher.lastName}
            </Link>
            {course.teacher.verified ? ' · Verificado' : ''}
          </p>
        ) : (
          <p>No pudimos mostrar quién da esta clase.</p>
        )}
        <p>{course.description}</p>
        {range && <p>Grados: {range}</p>}
        <p>
          {course.scheduleType === 'RECURRING' && course.schedule
            ? `${describeSchedule(course.schedule)} (${course.schedule.timezone})`
            : 'Horario por cita'}
          {course.durationMode === 'FIXED' && course.durationWeeks
            ? ` · ${course.durationWeeks} semanas`
            : ''}
        </p>
        <p>{course.duration} min por clase</p>
        <p>
          {course.availability === 'OPEN'
            ? 'Hay lugares'
            : course.availability === 'FULL'
            ? 'Sin lugares por ahora'
            : 'No pudimos comprobar los lugares'}
        </p>
        <p>
          {formatMoney(course.price, course.currency)} por sesión. Es solo informativo: tu familia
          decide.
        </p>
      </div>

      {course.overview && <Section title="De qué trata" text={course.overview} />}
      {!!course.learningObjectives?.length && (
        <div className="lrn-status">
          <h2>Qué vas a aprender</h2>
          <ul>
            {course.learningObjectives.map((objective) => (
              <li key={objective}>{objective}</li>
            ))}
          </ul>
        </div>
      )}
      {course.classStructure && <Section title="Cómo es la clase" text={course.classStructure} />}
      {course.whatToExpect && <Section title="Qué esperar" text={course.whatToExpect} />}

      <div className="lrn-status" aria-label="Pedir esta clase">
        {course.request && (
          <>
            <span className={`lrn-tag${open ? ' wait' : ''}`}>
              {REQUEST_STATE_WORDS[course.request.state]}
            </span>
            {course.request.reason && REQUEST_REASON_WORDS[course.request.reason] && (
              <p>{REQUEST_REASON_WORDS[course.request.reason]}</p>
            )}
          </>
        )}

        {!open && requestable && offer && (
          <>
            <button type="button" className="ag-b" disabled={sending} onClick={onRequest}>
              {REQUEST_THIS_CLASS}
            </button>
            <p>Esto le pregunta a tu familia. No compra nada.</p>
          </>
        )}

        {!open && !requestable && reason && <p>{blockExplanation(reason, course.gradeLevel)}</p>}

        {refusal && <p role="alert">{refusal}</p>}

        {!open && offer && (reason === 'SEAT_FULL' || course.scheduleType === 'RECURRING') && (
          <AnotherTime
            full={reason === 'SEAT_FULL'}
            interest={interest}
            onAsk={(why) =>
              onAskAnotherTime({
                reason: why,
                courseId: course.listingId,
                offerId: offer.offerId,
              })
            }
          />
        )}
      </div>

      <ReviewsSection
        subject="COURSE"
        subjectId={course.listingId}
        load={getCourseReviews}
        title="Reseñas de esta clase"
      />
    </>
  );
};

const Section: React.FC<{ title: string; text: string }> = ({ title, text }) => (
  <div className="lrn-status">
    <h2>{title}</h2>
    <p>{text}</p>
  </div>
);

/**
 * DEC-S2-S06-07 — it tells P06 the demand exists. It does not reserve, does not
 * charge and puts nobody in a line, and it says so.
 */
const AnotherTime: React.FC<{
  full: boolean;
  interest: 'idle' | 'sent' | 'failed';
  onAsk: (reason: 'SEAT_FULL' | 'TIME_MISMATCH') => void;
}> = ({ full, interest, onAsk }) => {
  if (interest === 'sent') {
    return <p role="status">Listo. Ya sabemos que te interesa. Esto no aparta ningún lugar.</p>;
  }
  return (
    <>
      <p>
        <button
          type="button"
          className="lrn-link lrn-btn"
          onClick={() => onAsk(full ? 'SEAT_FULL' : 'TIME_MISMATCH')}
        >
          Pedir otro horario
        </button>
      </p>
      {interest === 'failed' && <p role="alert">No pudimos guardarlo. Prueba otra vez.</p>}
    </>
  );
};

export default LearnerExploreCourse;
